import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { fetchData } from '../../../helpers/fetchData'
import CustomTable from '../../../shared/CustomTable'
import CustomPagination from '../../../shared/CustomPagination'
import CustomButton from '../../../shared/CustomButton'
import ReusableModal from '../../../shared/ReuseableModal'
import { EyeIcon } from '../../../utils/TableActionIcons'
import { MdRefresh, MdCheckCircle, MdWarning, MdStop } from 'react-icons/md'
import { formatDateTime } from '../../../utils/FormateDateTime'
import CustomChip from '../../../shared/CustomChip'
import { useGetAuditApprovalBatchesQuery } from '../../../redux/api/modules/batchesApi'
import CheckPermission from '../../../hooks/CheckPermission'

const AuditApprovalBatchesTab = () => {
  const { t } = useTranslation()
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [pollingJobId, setPollingJobId] = useState(null)
  const [stoppingJobId, setStoppingJobId] = useState(null)
  const { hasPermission } = CheckPermission()

  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    status: ''
  })

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetAuditApprovalBatchesQuery(filters)

  const loading = queryLoading || queryFetching

  const fetchJobStatus = async jobId => {
    try {
      const resp = await fetchData('GET', `/v3/invoices/audit-results/bulk-status/${jobId}`)
      if (resp) {
        setSelectedJob(resp)
        if (resp.status === 'processing' || resp.status === 'queued') {
          setTimeout(() => {
            if (pollingJobId === jobId) {
              fetchJobStatus(jobId)
            }
          }, 5000)
        } else {
          setPollingJobId(null)
          refetch()
        }
      }
    } catch (error) {
      console.error('Error fetching job status:', error)
      toast.error(t('Failed to fetch job status'))
      setPollingJobId(null)
    }
  }

  const handleStopJob = async job => {
    if (!hasPermission('cliamgeneration', 'update')) return
    if (!job?.job_id) return

    const canStop = ['processing', 'queued'].includes(job?.status?.toLowerCase())
    if (!canStop) {
      toast.error(t('Only processing or queued jobs can be stopped'))
      return
    }

    const confirmed = window.confirm(
      t('Are you sure you want to stop this audit job?')
    )
    if (!confirmed) return

    setStoppingJobId(job.job_id)
    try {
      const resp = await fetchData(
        'POST',
        `/v3/invoices/audit-results/bulk-stop/${job.job_id}`
      )

      toast.success(resp?.message || t('Audit job stopped successfully'))

      if (selectedJob?.job_id === job.job_id) {
        setSelectedJob(prev => ({
          ...prev,
          ...(resp || {}),
          status: resp?.status || 'cancelled'
        }))
      }

      if (pollingJobId === job.job_id) {
        setPollingJobId(null)
      }

      refetch()
      await fetchJobStatus(job.job_id)
    } catch (error) {
      console.error('Error stopping audit job:', error)
      toast.error(error?.message || t('Failed to stop audit job'))
    } finally {
      setStoppingJobId(null)
    }
  }

  useEffect(() => {
    return () => {
      setPollingJobId(null)
    }
  }, [])

  const onPageChange = (newPage, newRowsPerPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage,
      per_page: newRowsPerPage
    }))
  }

  const handleViewDetails = async job => {
    setSelectedJob(job)
    setShowDetailModal(true)
    if (job.status === 'processing' || job.status === 'queued') {
      setPollingJobId(job.job_id)
      fetchJobStatus(job.job_id)
    } else {
      fetchJobStatus(job.job_id)
    }
  }

  const getStatusChipType = status => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'processing':
        return 'warning'
      case 'queued':
      case 'pending':
        return 'default'
      case 'failed':
      case 'cancelled':
        return 'danger'
      default:
        return 'default'
    }
  }

  const action = {
    header: t('Actions'),
    body: rowData => (
      <div className='flex gap-2 items-center'>
        <EyeIcon
          onClick={() => handleViewDetails(rowData)}
          title={t('View Job Details')}
        />
        {hasPermission('cliamgeneration', 'update') &&
        ['processing', 'queued'].includes(rowData?.status?.toLowerCase()) && (
          <button
            type='button'
            onClick={() => handleStopJob(rowData)}
            disabled={stoppingJobId === rowData.job_id}
            title={t('Stop Audit Job')}
            className='text-red-600 hover:text-red-800 disabled:opacity-50'
          >
            <MdStop size={18} />
          </button>
        )}
      </div>
    )
  }

  const columns = [
    action,
    {
      header: t('Progress'),
      body: row => (
        <div className='flex items-center gap-2'>
          <span className='text-xs'>{row?.progress_percentage?.toFixed(0) || 0}%</span>
        </div>
      )
    },
    {
      header: t('Success'),
      body: row => (
        <span className='text-green-600 font-semibold'>{row.success_count || 0}</span>
      )
    },
    {
      header: t('Failed'),
      body: row => (
        <span className='text-red-600 font-semibold'>{row.failed_count || 0}</span>
      )
    },
    {
      header: t('Created At'),
      body: row => formatDateTime(row?.created_at) || '-'
    },
    {
      header: t('Completed At'),
      body: row => row?.completed_at ? formatDateTime(row?.completed_at) : '-'
    },
    {
      header: t('Status'),
      body: row => (
        <CustomChip
          label={row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1) || 'Unknown'}
          chipType={getStatusChipType(row?.status)}
          className='h-[22px] rounded-full px-2 py-1 h-[22px]'
        />
      )
    }
  ]

  return (
    <div>
      <div className='flex items-center justify-end mb-2'>
        <CustomButton
          title={t('Refresh')}
          icon='pi pi-refresh'
          buttonType='primary'
          className='w-fit h-[35px]'
          onClick={() => refetch()}
          loading={loading}
        />
      </div>

      <CustomTable
        data={data?.jobs || []}
        isLoading={loading}
        columns={columns}
        emptyMessage={t('No audit jobs found')}
      />

      <CustomPagination
        currentPage={data?.page || filters?.page || 1}
        rowsPerPage={data?.per_page || filters?.per_page || 20}
        totalRecords={data?.total_count || 0}
        totalPages={data?.total_pages || 1}
        onPageChange={onPageChange}
      />

      <ReusableModal
        isVisible={showDetailModal}
        title={t('Audit Job Details')}
        HeaderIcon={MdRefresh}
        onClose={() => {
          setShowDetailModal(false)
          setPollingJobId(null)
        }}
        className='w-full lg:w-[1000px]'
        position='top'
      >
        {selectedJob && (
          <div className='flex flex-col gap-4'>
            <div className='bg-gray-50 p-4 rounded-lg border border-gray-200'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Job ID')}:</span>
                  <span className='ml-2 font-mono text-sm'>{selectedJob.job_id}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Status')}:</span>
                  <span className='ml-2 px-2 py-1 rounded font-medium'>
                    {t(selectedJob.status)}
                  </span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Total Visits')}:</span>
                  <span className='ml-2 font-semibold'>{selectedJob.total_visits || 0}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Processed')}:</span>
                  <span className='ml-2 font-semibold'>{selectedJob.processed_count || 0}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Success')}:</span>
                  <span className='ml-2 font-semibold text-green-600'>{selectedJob.success_count || 0}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Failed')}:</span>
                  <span className='ml-2 font-semibold text-red-600'>{selectedJob.failed_count || 0}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Created At')}:</span>
                  <span className='ml-2'>{formatDateTime(selectedJob.created_at)}</span>
                </div>
                <div>
                  <span className='text-sm text-gray-600 font-medium'>{t('Completed At')}:</span>
                  <span className='ml-2'>{selectedJob.completed_at ? formatDateTime(selectedJob.completed_at) : '-'}</span>
                </div>
              </div>
              <div className='mt-4'>
                <div className='flex justify-between items-center mb-1'>
                  <span className='text-sm font-medium text-gray-700'>{t('Progress')}</span>
                  <span className='text-sm font-medium text-gray-700'>
                    {(selectedJob.progress_percentage || 0).toFixed(1)}%
                  </span>
                </div>
                <div className='w-full bg-gray-200 rounded-full h-3'>
                  <div
                    className='bg-primary h-3 rounded-full transition-all'
                    style={{ width: `${selectedJob.progress_percentage || 0}%` }}
                  />
                </div>
              </div>

              {selectedJob.estimated_completion_time && (
                <div className='mt-2'>
                  <span className='text-sm text-gray-600'>{t('Estimated Completion')}:</span>
                  <span className='ml-2 text-sm font-medium'>{formatDateTime(selectedJob.estimated_completion_time)}</span>
                </div>
              )}

              {['processing', 'queued'].includes(selectedJob?.status?.toLowerCase()) && (
                <div className='mt-3 flex items-center gap-2 text-sm text-blue-600'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary'></div>
                  <span>{t('Processing audit job...')}</span>
                </div>
              )}
            </div>

            {selectedJob.error_summary && Object.keys(selectedJob.error_summary).length > 0 && (
              <div className='bg-red-50 p-4 rounded-lg border border-red-200'>
                <h4 className='text-md font-semibold text-red-800 mb-2 flex items-center gap-2'>
                  <MdWarning />
                  {t('Error Summary')}
                </h4>
                <div className='space-y-1'>
                  {Object.entries(selectedJob.error_summary).map(([error, count]) => (
                    <div key={error} className='flex justify-between text-sm text-red-700'>
                      <span>{error}</span>
                      <span className='font-semibold'>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className='flex justify-end gap-2'>
              {['processing', 'queued'].includes(selectedJob?.status?.toLowerCase()) && (
                <CustomButton
                  title={t('Stop Job')}
                  buttonType='danger'
                  icon='pi pi-stop-circle'
                  onClick={() => handleStopJob(selectedJob)}
                  loading={stoppingJobId === selectedJob.job_id}
                />
              )}
              <CustomButton
                title={t('Close')}
                buttonType='secondary'
                onClick={() => {
                  setShowDetailModal(false)
                  setPollingJobId(null)
                }}
              />
            </div>
          </div>
        )}
      </ReusableModal>
    </div>
  )
}

export default AuditApprovalBatchesTab
